import { getValueByPointer } from 'fast-json-patch';
import _ from 'lodash';
import { Interpreter } from '@arcweave/arcscript';
import updater from '../../updater.js';
import { has } from '../../../../utils.js';
/**
 * Generic purpose getters that operate on a "flat" json structure.
 * The structure is an associative array (Object) that:
 * 1) Has the id of each node as its hash key (string)
 * 2) Contains an 'id' property that has the same value as the key
 */
function getters(options) {
  return {
    /**
     * Return the node given its id
     * @param   {String}  id    The node id
     * @return  {Object|null}   The node
     */
    getNode(state) {
      return id => {
        return state.byId[id] || null;
      };
    },

    /**
     * Return an array with the nodes from their ids
     * @param   {Array}   ids   The node ids
     * @return  {Array}         The nodes
     */
    getNodes(state) {
      return ids => {
        if (ids === null || ids === undefined) {
          return [];
        }

        return ids.map(id => state.byId[id]);
      };
    },

    /**
     * Returns all the nodes in the project
     * @return  {Array}         The nodes
     */
    getAllNodes(state) {
      return Object.values(state.byId);
    },

    /**
     * Returns all the IDs of the nodes
     * @returns {Array}         The node IDs
     */
    getNodeIds(state) {
      return Object.keys(state.byId);
    },

    /**
     * Return the container node of a node
     * @param   {String} id     The id of the node
     * @param   {String} type   The type of the parent
     * @param   {String} prop   The property name of the parent
     * @return  {Object|null}   The container node
     */
    getContainerNode(state, getters, rootState) {
      return (id, type, prop) => {
        return (
          Object.values(rootState.project.data[type].byId).find(
            node => has(node, prop) && node[prop].includes(id)
          ) || null
        );
      };
    },

    /**
     * Return the container nodes of a node
     * @param   {String} id     The id of the node
     * @param   {String} type   The type of the parent
     * @param   {String} prop   The property name of the parent
     * @return  {Array|null}    The container node ids
     */
    getContainerNodes(state, getters, rootState) {
      return (id, type, prop) => {
        const containerIds = [];
        let container = getters.getContainerNode(id, type, prop);
        while (container) {
          containerIds.push(container.id);
          container = getters.getContainerNode(container.id, type, prop);
        }
        return containerIds;
      };
    },

    /**
     * Return the contained nodes of a container
     * @param   {String} id     The id of the container node
     * @param   {String} type   The type of the nodes (usually same as the prop)
     * @param   {String} prop   The property name of the parent
     * @return  {Array}         The array with the contained nodes
     */
    getContainedNodes(state, getters, rootState) {
      return (id, type, prop) => {
        const node = state.byId[id];
        if (!node[prop]) return [];
        return node[prop].map(
          childId => rootState.project.data[type].byId[childId]
        );
      };
    },

    /**
     * Return the ids of all descendants of a node. Children and subchildren.
     * @param   {String} id     The id of the container node
     * @param   {String} type   The type of the nodes
     * @param   {String} prop   The property name of the parent
     * @return  {Array}         The array with the ids of the descendants
     */
    getDescendantNodes(state, getters, rootState) {
      return (id, type, prop) => {
        const node = state.byId[id];
        if (!getters.getIsRecursiveContainer(id)) return [];
        return node[prop].reduce((acc, cur) => {
          const descend = getters.getDescendantNodes(cur, type, prop);
          return [
            ...new Set([
              ...acc,
              ...getters.getDescendantNodes(cur, type, prop),
            ]),
          ];
        }, _.cloneDeep(node[prop]));
      };
    },

    /**
     * Return the index of a node in a container
     * @param   {String} id     The id of the node
     * @param   {String} type   The type of the parent
     * @param   {String} prop   The property name of the parent
     * @return  {Number}        The index in the container
     */
    getIndex(state, getters) {
      return (id, type, prop) => {
        const container = getters.getContainerNode(id, type, prop);
        if (!container) return null;
        return container[prop].indexOf(id);
      };
    },

    /**
     * Return whether the node is a recursive container (has 'children' property)
     * @param   {String}  id    The id of the node
     * @return  {Boolean}       Whether the node is a recursive container
     */
    getIsRecursiveContainer(state, getters) {
      return id => {
        const node = state.byId[id];
        return has(node, 'children');
      };
    },

    /**
     * Return the root node if the structure contains recursive containers.
     * This is the node with property root by convention
     * @return  {String}    The id of the root node
     */
    getRootContainer(state, getters) {
      const root = Object.values(state.byId).find(node => has(node, 'root'));
      return root;
    },

    /**
     * Return the next (non-container) node in a recursive container from the given one.
     * @param  {String}   id          The id of the node
     * @return {String|null}          The id of the next node, null if none exists
     */
    getNextNode(state, getters) {
      return id => {
        const { type, prop } = options.container;
        const container = getters.getContainerNode(id, type, prop);
        if (!container) return null;
        const index = getters.getIndex(id, type, prop);
        return (
          container[prop]
            .slice(index + 1)
            .filter(item => !getters.getIsRecursiveContainer(item))
            .shift() || null
        );
      };
    },

    /**
     * Return the previous (non-container) node in a recursive container from the given one.
     * @param  {String}   id          The id of the node
     * @return {String|null}          The id of the previous node, null if none exists
     */
    getPreviousNode(state, getters) {
      return id => {
        const { type, prop } = options.container;
        const container = getters.getContainerNode(id, type, prop);
        if (!container) return null;
        const index = getters.getIndex(id, type, prop);
        return (
          container[prop]
            .slice(0, index)
            .reverse()
            .filter(item => !getters.getIsRecursiveContainer(item))
            .shift() || null
        );
      };
    },

    /**
     * Return the next available (non-container) node in a recursive container from the given one.
     * 1) First we search in the same container as the given one's next nodes
     * 2) If none we search in the same container as the given one's previous nodes
     * 2) If none we recursively perform the same search on the parent container
     * 3) If none we return the first non-container node (in state)
     * 4) If none we return null
     * @param {String}  id    The id of the node
     * @return {String|null}  The id of the next available node, null if none exists
     */
    getNextAvailableNode(state, getters) {
      return id => {
        const nextNode = getters.getNextNode(id);
        if (nextNode) return nextNode;
        const previousNode = getters.getPreviousNode(id);
        if (previousNode) return previousNode;
        const { type, prop } = options.container;
        const container = getters.getContainerNode(id, type, prop);
        if (container) {
          const node = getters.getNextAvailableNode(container.id);
          if (node) return node;
        }
        if (!getters.getIsRecursiveContainer(id)) {
          const oneNode = Object.keys(state.byId).find(
            item => !getters.getIsRecursiveContainer(item) && item !== id
          );
          if (oneNode) return oneNode;
        }
        return null;
      };
    },
  };
}

function actions(options) {
  return {
    /**
     * Create a node and optionally put it at a specified position in a container
     * @param {Object}  node            The node that will be created
     * @param {String}  tId             The id of the container that will host the node
     * @param {Object}  container       An object containing info on the container
     * @param {String}  container.type  The type of the container
     * @param {String}  container.prop  The prop of the container that will host the node
     * @param {Number}  tIndex          The index in the 'children' array of the container
     * @param {Boolean} forceIndex      Force parent reference index
     * @param {Boolean} apply           Whether to apply the patch after finishing
     * @param {String}  movedId         Whether the creation comes after a move (useful in connections)
     */
    create(
      context,
      {
        node,
        tId,
        container,
        tIndex,
        forceIndex = false,
        apply,
        movedId = null,
      }
    ) {
      const patch = [
        // Add node data to byId
        {
          op: 'add',
          path: `/data/${options.type}/byId/${node.id}`,
          value: node,
          ...(movedId && { discardId: movedId }),
        },
      ];
      // Add node reference to container (at the specified index)
      if (container || (options.container && options.container.type)) {
        let type;
        let prop;
        if (container) {
          type = container.type;
          prop = container.prop;
        } else {
          type = options.container.type;
          prop = options.container.prop;
        }
        let state = [];
        if (
          context.rootState.project.data[type].byId[tId] &&
          has(context.rootState.project.data[type].byId[tId], prop)
        ) {
          state = getValueByPointer(
            context.rootState.project,
            `/data/${type}/byId/${tId}/${prop}`
          );
          patch.push({
            op: 'add',
            path: `/data/${type}/byId/${tId}/${prop}/${tIndex}`,
            value: node.id,
            state,
            ...(movedId && { discardId: movedId }),
          });
        } else if (forceIndex) {
          patch.push({
            op: 'add',
            path: `/data/${type}/byId/${tId}/${prop}/${tIndex}`,
            value: node.id,
            ...(movedId && { discardId: movedId }),
          });
        } else {
          patch.push({
            op: 'add',
            path: `/data/${type}/byId/${tId}/${prop}`,
            value: [node.id],
            ...(movedId && { discardId: movedId }),
          });
        }
      }

      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Create a series of nodes and optionally put them in the specified container
     * @param {Array}   nodes     The nodes that will be created
     * @param {String}  tId       The id of the container that will host the nodes
     * @param {Number}  tIndex    The index in the 'children' array of the container
     * @param {Boolean} apply     Whether to apply the patch after finishing
     */
    createMany(context, { nodes, tId, tIndex, apply }) {
      nodes.forEach((node, index) => {
        context.dispatch('create', {
          node,
          tId,
          tIndex: tIndex === '-' ? tIndex : tIndex + index,
          apply: false,
        });
      });

      updater.complete({ apply });
    },

    /**
     * Remove a node with the specified id
     * @param {String}  id        The id of the node
     * @param {Boolean} apply     Whether to apply the patch after finishing
     */
    delete(context, { id, apply, deleteReference = true }) {
      const patch = [
        // Remove node from byId structure
        {
          op: 'remove',
          path: `/data/${options.type}/byId/${id}`,
        },
      ];
      // Remove node reference from its container
      if (options.container && deleteReference) {
        let { type } = options.container;
        const { prop } = options.container;
        const node = context.getters.getNode(id);
        let container;
        let cIndex;
        if (has(node, 'cId') && has(node, 'cType')) {
          type = node.cType;
          container = context.rootGetters['project/data/getNodeOfType'](
            node.cId,
            node.cType
          );
          cIndex = container[prop].indexOf(id);
        } else {
          container = context.getters.getContainerNode(id, type, prop);
          cIndex = context.getters.getIndex(id, type, prop);
        }

        if (container && cIndex !== null) {
          const state = getValueByPointer(
            context.rootState.project,
            `/data/${type}/byId/${container.id}/${prop}`
          );
          patch.push({
            op: 'remove',
            path: `/data/${type}/byId/${container.id}/${prop}/${cIndex}`,
            state,
          });
        }
      }
      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Remove a series of nodes with the specified ids
     * @param {Array}   ids     The ids of the nodes
     * @param {Boolean} apply   Whether to apply the patch after finishing
     */
    deleteMany(context, { ids, apply, deleteReference = true }) {
      // We should put the nodes in reverse order of parent index
      if (deleteReference) {
        ids.sort((ida, idb) => {
          if (!options.container) return 0;
          const { type, prop } = options.container;
          const indexa = context.getters.getIndex(ida, type, prop);
          const indexb = context.getters.getIndex(idb, type, prop);
          return indexb - indexa;
        });
      }
      ids.forEach(id => {
        context.dispatch('delete', { id, deleteReference, apply: false });
      });
      updater.complete({ apply });
    },

    /**
     * Update some of the node's properties.
     * @param {String}  id      The id that should be updated
     * @param {Object}  props   Only the properties that are changed
     * @param {Boolean} apply   Whether to apply the patch after finishing
     */
    update(context, { id, props, apply }) {
      console.log('Basic update is called with props:', props);
      const patch = [];
      Object.keys(props).forEach(prop => {
        // Replace component property with new value
        patch.push({
          op: 'add',
          path: `/data/${options.type}/byId/${id}/${prop}`,
          value: props[prop],
        });
      });
      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Delete a specific node property.
     * @param {String}  id      The node id
     * @param {String}  prop    The property name
     * @param {Boolean} apply   Whether to apply the patch after finishing
     */
    deleteProperty(context, { id, prop, apply }) {
      const patch = [
        // Remove node property
        {
          op: 'remove',
          path: `/data/${options.type}/byId/${id}/${prop}`,
        },
      ];
      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Move a node to another position in the same type
     * @param {String}  id       The node to be moved
     * @param {String}  tId      The container node that will host it
     * @param {Number}  tIndex   The index in the container
     * @param {boolean} adjust   Whether we might need to adjust τΙndex (if it takes into account starting position)
     * @param {Boolean} apply    Whether to apply the patch after finishing
     */
    move(context, { id, tId, tIndex, adjust = true, apply }) {
      let { type } = options.container;
      const { prop } = options.container;
      const node = context.getters.getNode(id);
      let container;
      let index;
      if (has(node, 'cId') && has(node, 'cType')) {
        type = node.cType;
        container = context.rootGetters['project/data/getNodeOfType'](
          node.cId,
          node.cType
        );
        index = container[prop].indexOf(id);
      } else {
        container = context.getters.getContainerNode(id, type, prop);
        index = context.getters.getIndex(id, type, prop);
      }
      // Json patch Move performs a remove operation first and an add operation later
      // so we need to adjust the target index
      if (container.id === tId && index < tIndex && adjust) {
        tIndex -= 1;
      }
      // Create also the fromState
      const fromState = getValueByPointer(
        context.rootState.project,
        `/data/${type}/byId/${container.id}/${prop}`
      );

      const state = getValueByPointer(
        context.rootState.project,
        `/data/${type}/byId/${tId}/${prop}`
      );

      const patch = [
        // Move to the target position
        {
          op: 'move',
          from: `/data/${type}/byId/${container.id}/${prop}/${index}`,
          path: `/data/${type}/byId/${tId}/${prop}/${tIndex}`,
          fromState,
          state,
        },
      ];
      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Create a reference to an item. If the property that will hold the reference does not exist it will be created.
     * @param {array}   ids     The referenced objects
     * @param {String}  rId     The referencing object
     * @param {String}  rType   The type of the referencing object
     * @param {String}  rProp   The property in the referencing object
     * @param {Boolean} apply   Whether to apply the patch after finishing
     */
    createReferences(context, { ids, rId, rType, rProp, apply }) {
      const rObject = context.rootState.project.data[rType].byId[rId];
      const patch = [];
      // Check if the property that will hold the reference exists
      if (!rObject || !has(rObject, rProp)) {
        // Create the property (as an array) of references
        patch.push({
          op: 'add',
          path: `/data/${rType}/byId/${rId}/${rProp}`,
          value: ids,
        });
      } else {
        const state = getValueByPointer(
          context.rootState.project,
          `/data/${rType}/byId/${rId}/${rProp}`
        );
        ids.forEach(id => {
          // Add the references at the end
          patch.push({
            op: 'add',
            path: `/data/${rType}/byId/${rId}/${rProp}/-`,
            value: id,
            state,
          });
        });
      }

      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Rename the variables in arcscript in properties in all nodes of this type.
     * @param {Array}   props           An array of the property names to look for arcscript code
     * @param {Object}  variableRenames An object with key the variable ID and value the new variable name
     * @param {Object}  variableObjects The variable objects before renaming the variable
     * @param {boolean} apply           Whether to apply the patch after finishing
     */
    renameVariables(
      context,
      { props = [], variableRenames = {}, variableObjects, apply }
    ) {
      if (props.length === 0 || Object.keys(variableRenames).length === 0) {
        return;
      }

      const { type } = options;
      const patch = [];
      const nodes = context.rootState.project.data[type].byId;

      Object.values(nodes).forEach(node => {
        props.forEach(prop => {
          const propValue = node[prop];

          if (propValue) {
            let resultValue = '';
            const i = new Interpreter({}, variableObjects, {}, '');
            if (type === 'conditions') {
              resultValue = i.replaceVariables(
                `<pre><code>${propValue}</code></pre>`,
                variableRenames
              );
              resultValue = resultValue.substring(11, resultValue.length - 13);
            } else {
              try {
                resultValue = i.replaceVariables(propValue, variableRenames);
              } catch (error) {
                console.error('Error while replacing variable name: ', error);
                return;
              }
            }

            if (resultValue !== propValue) {
              patch.push({
                op: 'replace',
                path: `/data/${type}/byId/${node.id}/${prop}`,
                value: resultValue,
              });
            }
          }
        });
      });
      updater.add(patch);
      updater.complete({ apply });
    },
  };
}

export default { getters, actions };
